import { Check, ChevronDown, Globe } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { LANGUAGES, DEFAULT_LANGUAGE, localizePath, stripLangFromPath } from '../i18n/config';

/**
 * 语言切换器 — 支持 12 种语言，RTL 语言（ar/fa）自动切换布局方向。
 * 切换语言时保留当前页面路径，仅替换语言前缀。
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (langCode: string) => {
    if (langCode === i18n.language) {
      setOpen(false);
      return;
    }
    const currentPathWithoutLang = stripLangFromPath(location.pathname);
    const newPath = localizePath(currentPathWithoutLang, langCode);
    i18n.changeLanguage(langCode);
    document.documentElement.lang = langCode;
    document.documentElement.dir = LANGUAGES.find((l) => l.code === langCode)?.dir || 'ltr';
    navigate(newPath);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-aqua hover:text-aqua"
        aria-label="Change language"
        aria-expanded={open}
      >
        <Globe size={16} />
        <span>{currentLang.localName}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div
          className="absolute end-0 z-50 mt-2 max-h-96 w-56 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-panel"
          role="menu"
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === i18n.language;
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitem"
                onClick={() => handleChange(lang.code)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-semibold transition hover:bg-slate-50 ${
                  isActive ? 'bg-aqua/10 text-aqua' : 'text-slate-700'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base leading-none">{lang.localName}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{lang.name}</span>
                  {isActive ? <Check size={14} className="text-aqua" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
